import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  TextField,
  Button,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import PercentIcon from "@mui/icons-material/Percent";
import PaymentsIcon from "@mui/icons-material/Payments";
import { toast } from "react-toastify";
import api from "../../axiosInstance";
import { BUTTON_STYLES } from "../../styles/componentStyles";

// Friendly labels + icons for each lookupCode
const CONFIG_META = {
  PLATFORM_FEE: { label: "Platform Fee", icon: <PaymentsIcon /> },
  GST: { label: "GST", icon: <PercentIcon /> },
};

function MentorConfigModal({ open, onClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [configs, setConfigs] = useState([]); // full row objects (keep id, lookupCode, ...)
  const [values, setValues] = useState({}); // { [id]: editableValue }
  const [editingId, setEditingId] = useState(null); // which row is in edit mode
  const [savingId, setSavingId] = useState(null);

  // Fetch the mentor config lookups whenever the modal opens
  useEffect(() => {
    if (!open) return;
    setEditingId(null);
    setIsLoading(true);
    api
      .get(`/api/v1/lookups/mentorConfig`)
      .then((res) => {
        const rows = res.data.data || [];
        setConfigs(rows);
        setValues(
          rows.reduce((acc, row) => {
            acc[row.id] = row.lookupValue;
            return acc;
          }, {}),
        );
      })
      .catch((error) => {
        console.error("Failed to fetch mentor config", error);
        toast.error("Failed to load mentor config. Please try again.");
      })
      .finally(() => setIsLoading(false));
  }, [open]);

  const handleChange = (id, value) => {
    setValues((prev) => ({ ...prev, [id]: value }));
  };

  // Cancel edit: restore original value
  const handleCancelEdit = (row) => {
    setValues((prev) => ({ ...prev, [row.id]: row.lookupValue }));
    setEditingId(null);
  };

  // Update a single value by its id
  const handleSave = async (row) => {
    setSavingId(row.id);
    try {
      const response = await api.put(`/api/v1/lookups/${row.id}/value`, {
        lookup_value: String(values[row.id]),
      });
      const updated = response.data.data;
      setConfigs((prev) =>
        prev.map((c) => (c.id === row.id ? { ...c, ...updated } : c)),
      );
      toast.success(
        `${CONFIG_META[row.lookupCode]?.label || row.lookupCode} updated!`,
        { position: "top-right", autoClose: 2000 },
      );
      setEditingId(null);
    } catch (error) {
      console.error("Update failed", error);
      toast.error("Failed to update. Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableScrollLock
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Mentor Configuration
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {configs.map((row) => {
              const meta = CONFIG_META[row.lookupCode] || {
                label: row.lookupCode,
                icon: <PaymentsIcon />,
              };
              const isEditing = editingId === row.id;
              return (
                <Paper
                  key={row.id}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2 }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* Icon + label */}
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          display: "flex",
                          color: "#17479e",
                        }}
                      >
                        {meta.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {meta.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.lookupCode} · ID: {row.id}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Value: read-only view OR edit field */}
                    {isEditing ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          type="number"
                          size="small"
                          autoFocus
                          value={values[row.id] ?? ""}
                          onChange={(e) => handleChange(row.id, e.target.value)}
                          sx={{ width: 110 }}
                        />
                        <Tooltip title="Save" arrow>
                          <span>
                            <IconButton
                              color="primary"
                              disabled={savingId === row.id}
                              onClick={() => handleSave(row)}
                            >
                              {savingId === row.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <SaveIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel" arrow>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleCancelEdit(row)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {row.lookupValue}
                        </Typography>
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => setEditingId(row.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MentorConfigModal;
